import React, { useState } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Plus, Trash2, Check, Pencil, X, Copy } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Gerenciador de peças de um tipo de carcaça (dentro de "Calibrar tipo"):
// adicionar (código + nome), editar, remover. O mesmo código pode ser usado por nomes diferentes.
// sources: [{ key, label, pieces:[{name, code}] }] — outros tipos de onde copiar a lista
export const TypePiecesManager = ({ carcassType, pieces, onSaved, productList = [], sources = [] }) => {
  const [copyFrom, setCopyFrom] = useState('');
  const [rows, setRows] = useState(pieces.map((p) => ({ name: p.name, code: p.code || '' })));
  const [newRow, setNewRow] = useState({ name: '', code: '' });
  const [editIdx, setEditIdx] = useState(null);
  const [editRow, setEditRow] = useState({ name: '', code: '' });
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const [open, setOpen] = useState(false);

  const productFor = (code) => {
    const c = (code || '').trim();
    if (!c) return null;
    return productList.find((x) => x.code === c || x.barcode === c) || null;
  };

  const persist = async (next, okMsg = 'Peças salvas.') => {
    setSaving(true); setMsg('');
    try {
      const r = await axios.put(`${API}/beef/type-pieces/${carcassType}`, { pieces: next }, { withCredentials: true });
      setRows(r.data.pieces.map((p) => ({ name: p.name, code: p.code || '' })));
      onSaved?.(r.data.pieces);
      setMsg(r.data.missing_products?.length ? `${okMsg} Código sem produto cadastrado: ${r.data.missing_products.map((m) => `${m.code} (${m.name})`).join(', ')}` : okMsg);
    } catch (e) { setMsg(e.response?.data?.detail || 'Erro ao salvar peças'); } finally { setSaving(false); }
  };

  const add = () => {
    const name = newRow.name.trim();
    if (!name) return;
    if (rows.some((r) => r.name.toLowerCase() === name.toLowerCase())) { setMsg('Já existe uma peça com esse nome.'); return; }
    const next = [...rows, { name, code: newRow.code.trim() }];
    setNewRow({ name: '', code: '' });
    persist(next);
  };
  const remove = (i) => { if (!window.confirm(`Remover a peça "${rows[i].name}" deste tipo?`)) return; persist(rows.filter((_, k) => k !== i)); };
  const startEdit = (i) => { setEditIdx(i); setEditRow({ ...rows[i] }); };
  const confirmEdit = () => {
    const name = editRow.name.trim();
    if (!name) return;
    const next = rows.map((r, k) => (k === editIdx ? { name, code: editRow.code.trim() } : r));
    setEditIdx(null);
    persist(next);
  };

  // Copiar peças de outro tipo: acrescenta as que ainda não existem (mesmo nome) — sem apagar as atuais
  const copyFromType = (mode) => {
    const src = sources.find((t) => t.key === copyFrom);
    if (!src) return;
    const existing = new Set(rows.map((r) => r.name.toLowerCase()));
    const incoming = src.pieces.map((p) => ({ name: p.name, code: p.code || '' }));
    const next = mode === 'replace' ? incoming : [...rows, ...incoming.filter((p) => !existing.has(p.name.toLowerCase()))];
    const added = mode === 'replace' ? incoming.length : next.length - rows.length;
    if (mode === 'replace' && !window.confirm(`Substituir as ${rows.length} peça(s) atuais pelas ${incoming.length} de "${src.label}"?`)) return;
    if (mode === 'merge' && added === 0) { setMsg(`Todas as peças de ${src.label} já existem neste tipo.`); return; }
    persist(next, mode === 'replace' ? `Lista substituída pelas ${incoming.length} peças de ${src.label}.` : `${added} peça(s) copiada(s) de ${src.label}.`);
  };

  return (
    <div className="border border-slate-200 rounded-md mb-4" data-testid="type-pieces-manager">
      <button type="button" onClick={() => setOpen(!open)} className="w-full flex items-center justify-between px-3 py-2 text-sm font-bold text-slate-800 bg-slate-50 hover:bg-slate-100 rounded-t-md" data-testid="type-pieces-toggle">
        <span>Gerenciar peças deste tipo ({rows.length})</span>
        <span className="text-xs font-normal text-slate-500">{open ? 'ocultar' : 'adicionar / editar / remover'}</span>
      </button>
      {open && (
        <div className="p-3 space-y-2">
          <p className="text-xs text-slate-500">O <b>código</b> é o código de balança/venda do produto já cadastrado — o mesmo código pode ser usado em peças com nomes diferentes (ex.: "acém" e "acém/paleta" → mesmo produto). Não cria produtos novos.</p>
          <div className="max-h-56 overflow-y-auto divide-y divide-slate-100">
            {rows.map((r, i) => {
              const prod = productFor(r.code);
              return (
                <div key={`${r.name}-${i}`} className="flex items-center gap-2 py-1.5" data-testid={`type-piece-row-${i}`}>
                  {editIdx === i ? (
                    <>
                      <Input value={editRow.code} onChange={(e) => setEditRow({ ...editRow, code: e.target.value })} placeholder="código" className="w-28 h-8 text-sm" data-testid="type-piece-edit-code" />
                      <Input value={editRow.name} onChange={(e) => setEditRow({ ...editRow, name: e.target.value })} placeholder="nome da peça" className="flex-1 h-8 text-sm" data-testid="type-piece-edit-name" onKeyDown={(e) => e.key === 'Enter' && confirmEdit()} />
                      <button type="button" onClick={confirmEdit} className="text-emerald-600 hover:text-emerald-800" title="Confirmar" data-testid="type-piece-edit-ok"><Check size={16} /></button>
                      <button type="button" onClick={() => setEditIdx(null)} className="text-slate-400 hover:text-slate-700" title="Cancelar"><X size={16} /></button>
                    </>
                  ) : (
                    <>
                      <span className="w-28 text-xs font-mono text-slate-600 truncate">{r.code || <span className="text-amber-600">sem código</span>}</span>
                      <span className="flex-1 text-sm capitalize truncate">{r.name}{prod && <span className="text-[11px] text-emerald-700 ml-1">→ {prod.name}</span>}</span>
                      <button type="button" onClick={() => startEdit(i)} className="text-blue-600 hover:text-blue-800" title="Editar" data-testid={`type-piece-edit-${i}`}><Pencil size={14} /></button>
                      <button type="button" onClick={() => remove(i)} className="text-red-600 hover:text-red-800" title="Remover" data-testid={`type-piece-remove-${i}`}><Trash2 size={14} /></button>
                    </>
                  )}
                </div>
              );
            })}
          </div>
          {sources.length > 0 && (
            <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-slate-200" data-testid="type-pieces-copy">
              <span className="text-xs text-slate-600">Copiar peças de:</span>
              <select value={copyFrom} onChange={(e) => setCopyFrom(e.target.value)} className="h-8 px-2 rounded-md border border-slate-300 bg-white text-sm" data-testid="type-pieces-copy-select">
                <option value="">selecione o tipo…</option>
                {sources.map((t) => <option key={t.key} value={t.key}>{t.label} ({t.pieces.length})</option>)}
              </select>
              <Button type="button" size="sm" variant="outline" onClick={() => copyFromType('merge')} disabled={saving || !copyFrom} className="h-8" title="Acrescenta as peças que ainda não existem neste tipo" data-testid="type-pieces-copy-merge"><Copy size={14} className="mr-1" /> Copiar (juntar)</Button>
              <Button type="button" size="sm" variant="outline" onClick={() => copyFromType('replace')} disabled={saving || !copyFrom} className="h-8 text-amber-700 border-amber-300" title="Substitui a lista atual pela do tipo escolhido" data-testid="type-pieces-copy-replace">Substituir</Button>
            </div>
          )}
          <div className="flex items-center gap-2 pt-2 border-t border-slate-200">
            <Input value={newRow.code} onChange={(e) => setNewRow({ ...newRow, code: e.target.value })} placeholder="código" className="w-28 h-8 text-sm" data-testid="type-piece-new-code" />
            <Input value={newRow.name} onChange={(e) => setNewRow({ ...newRow, name: e.target.value })} placeholder="nova peça (ex.: cupim)" className="flex-1 h-8 text-sm" data-testid="type-piece-new-name" onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), add())} />
            <Button type="button" size="sm" onClick={add} disabled={saving || !newRow.name.trim()} className="bg-red-600 hover:bg-red-700 h-8" data-testid="type-piece-add"><Plus size={14} className="mr-1" /> Adicionar</Button>
          </div>
          {msg && <p className="text-xs text-slate-600" data-testid="type-pieces-msg">{msg}</p>}
        </div>
      )}
    </div>
  );
};
